import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';

import { elementStyle, StripeField } from '@/features/payments';

import type { MethodCompletion } from './useMethodCompletion';

interface NewCardFieldsProps {
  cardholderName: string;
  onCardholderNameChange: (name: string) => void;
  saveCard: boolean;
  onSaveCardChange: (save: boolean) => void;
  completion: MethodCompletion;
  disabled: boolean;
}

/**
 * Card entry for a one-off card.
 *
 * The card number takes the full width and expiry/CVC share a row: at 360px
 * three fields abreast — the legacy desktop layout — leaves the number field
 * too narrow to read back what was typed.
 *
 * Elements carry `id`s because `StripeField` labels them with `htmlFor`; that
 * is also what the legacy `MutationObserver` keyed on, though completion now
 * comes from each Element's own `onChange`.
 */
export const NewCardFields = ({
  cardholderName,
  onCardholderNameChange,
  saveCard,
  onSaveCardChange,
  completion,
  disabled,
}: NewCardFieldsProps) => (
  <div className="flex flex-col gap-3">
    <TextField
      label="Name on card"
      size="small"
      value={cardholderName}
      onChange={(event) => {
        onCardholderNameChange(event.target.value);
      }}
      autoComplete="cc-name"
      placeholder="John Smith"
      disabled={disabled}
      slotProps={{ inputLabel: { shrink: true } }}
    />

    <StripeField label="Card number" htmlFor="cardNumber">
      {(handlers) => (
        <CardNumberElement
          id="cardNumber"
          options={{ style: elementStyle, showIcon: true, disabled }}
          onChange={(event) => {
            completion.onCardNumberChange(event.complete);
          }}
          {...handlers}
        />
      )}
    </StripeField>

    <div className="flex gap-3">
      <StripeField label="Expiry" htmlFor="cardExpiry" className="flex-1">
        {(handlers) => (
          <CardExpiryElement
            id="cardExpiry"
            options={{ style: elementStyle, disabled }}
            onChange={(event) => {
              completion.onCardExpiryChange(event.complete);
            }}
            {...handlers}
          />
        )}
      </StripeField>

      <StripeField label="CVC" htmlFor="cardCvc" className="flex-1">
        {(handlers) => (
          <CardCvcElement
            id="cardCvc"
            options={{ style: elementStyle, disabled }}
            onChange={(event) => {
              completion.onCardCvcChange(event.complete);
            }}
            {...handlers}
          />
        )}
      </StripeField>
    </div>

    <FormControlLabel
      control={
        <Checkbox
          checked={saveCard}
          onChange={(event) => {
            onSaveCardChange(event.target.checked);
          }}
          disabled={disabled}
        />
      }
      label="Save this card for future payments"
    />
  </div>
);
